import { Box, Card, Text, theme } from "@aksara-ui/react";
import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";

import GoldMedal from "assets/images/icons/gold-medal.png";
import SilverMedal from "assets/images/icons/silver-medal.png";
import BronzeMedal from "assets/images/icons/bronze-medal.png";
import CommonMedal from "assets/images/icons/common-medal.png";
import DiscordChat from "assets/images/icons/discord-chat.png";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import styled from "styled-components";
import { InputSelect } from "components/Form/InputSelect";
import { useDispatch, useSelector } from "react-redux";
import {
  getMembershipLeaderboard,
  getTotalMembersNftClaimed,
  getTotalMembersVerifiedNumber,
} from "redux/actions";
import ChartDataLabels from "chartjs-plugin-datalabels";

const HeaderWrapper = styled(Box)`
  justify-content: space-between;
  margin-bottom: 30px;
  gap: 20px;
`;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

interface ValueSchema {
  label: string;
  value: string | number;
}

const MemberActivityContent: React.FC = () => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 576 });

  const [type, setType] = useState<ValueSchema | null>(null);

  const [typeItems] = React.useState([
    { label: "Month", value: "month" },
    { label: "Day", value: "day" },
    { label: "Year", value: "year" },
  ]);

  const {
    totalMembersRegistered,
    totalMembersVerifiedNumber,
    totalMembersNftClaimed,
    totalMembersNftNotClaimed,
    membershipLeaderboards,
  } = useSelector((state: any) => state.dashboardAnalyticReducer);

  const memberRegistrationOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: "right" as const,
      },
      title: {
        display: true,
      },
      datalabels: {
        color: theme.colors.gray10,
        font: {
          weight: "bold",
          size: 12,
        },
        formatter: (value: any, context: any) => {
          const dataset = context.dataset.data;
          const total = dataset.reduce(
            (acc: number, curr: number) => acc + curr,
            0
          );
          const percentage = ((value / total) * 100).toFixed(2) + "%";
          return percentage;
        },
      },
    },
  };

  const memberRegistrationData = {
    labels: ["Not Verified", "Verified"],
    datasets: [
      {
        label: "Member Registration",
        data: [totalMembersRegistered, totalMembersVerifiedNumber],
        // data dummy: [40, 60],
        backgroundColor: ["rgba(220, 221, 225, 1)", "rgba(153, 128, 250, 1)"],
      },
    ],
  };

  const nftClaimOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: "right" as const,
      },
      title: {
        display: true,
      },
      datalabels: {
        color: theme.colors.gray10,
        font: {
          weight: "bold",
          size: 12,
        },
        formatter: (value: any, context: any) => {
          const dataset = context.dataset.data;
          const total = dataset.reduce(
            (acc: number, curr: number) => acc + curr,
            0
          );
          const percentage = ((value / total) * 100).toFixed(2) + "%";
          return percentage;
        },
      },
    },
  };

  const nftClaimData = {
    labels: ["Non NFT Holder", "NFT Holder"],
    datasets: [
      {
        label: "NFT Holder",
        data: [totalMembersNftNotClaimed, totalMembersNftClaimed],
        // data dummy: [60, 40],
        backgroundColor: ["rgba(220, 221, 225, 1)", "rgba(253, 167, 223, 0.8)"],
      },
    ],
  };

  useEffect(() => {
    dispatch(getTotalMembersVerifiedNumber() as any);
    dispatch(getTotalMembersNftClaimed() as any);
    dispatch(getMembershipLeaderboard() as any);
  }, [dispatch]);

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      <HeaderWrapper display={"flex"} flexDirection={"column"}>
        <Box display={"flex"}>
          <Text scale={[500, 600]} fontWeight={700}>
            Member Activity Summary
          </Text>
        </Box>

        <Box display={"flex"} alignItems={"center"}>
          <Text color="grey07" fontWeight={700} marginRight={10}>
            View By
          </Text>
          <Box width={"136px"}>
            <InputSelect
              selectedItem={type}
              placeholder="Select Type"
              handleSelectedItemChange={({ selectedItem }) => {
                if (selectedItem) {
                  setType(selectedItem);
                }
              }}
              itemToString={(item) => (item ? `${item.label}` : "")}
              itemValue={(item) => (item ? `${item.value}` : "")}
              items={typeItems}
              size={"lg"}
              borderRadius={"6px"}
            />
          </Box>
        </Box>
      </HeaderWrapper>

      <Box
        display={"flex"}
        justifyContent={"space-between"}
        flexDirection={isMobile ? "column" : "row"}
        marginTop={50}
        marginBottom={80}
        style={{ gap: 30 }}
      >
        <Box width={isMobile ? "100%" : "40%"} height={300}>
          <Text color="grey07" scale={400} fontWeight={"bold"}>
            MEMBER REGISTRATION
          </Text>

          <Pie
            options={memberRegistrationOptions as any}
            data={memberRegistrationData}
          />
        </Box>

        <Box width={isMobile ? "100%" : "40%"} height={300}>
          <Text color="grey07" scale={400} fontWeight={"bold"}>
            NFT HOLDER
          </Text>

          <Pie options={nftClaimOptions as any} data={nftClaimData} />
        </Box>
      </Box>

      <Box
        display={"flex"}
        justifyContent={"space-between"}
        flexDirection={isMobile ? "column" : "row"}
        marginY={50}
        style={{ gap: 30 }}
      >
        <Box
          display={"flex"}
          flexDirection={"column"}
          width={isMobile ? "100%" : "40%"}
          style={{ gap: 10 }}
        >
          <Text color="grey07" scale={400} fontWeight={"bold"}>
            LATEST CONVERSATION
          </Text>
          <Text
            color="grey07"
            scale={400}
            fontWeight={"bold"}
            marginBottom={10}
          >
            Channel: #giveaway
          </Text>

          <img src={DiscordChat} height={"80%"} alt="discord-chat" />
        </Box>

        <Box
          display={"flex"}
          flexDirection={"column"}
          width={isMobile ? "100%" : "50%"}
          style={{ gap: 10 }}
        >
          <Text color="grey07" scale={400} fontWeight={"bold"}>
            LEADERBOARD
          </Text>
          <Text
            color="grey07"
            scale={400}
            fontWeight={"bold"}
            marginBottom={10}
          >
            Channel: All Channel
          </Text>

          {membershipLeaderboards.map((user: any, index: any) => (
            <Card
              key={index}
              p={"md"}
              style={{ border: "1px solid #C6D0DB", borderRadius: 30, gap: 10 }}
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
            >
              {index === 0 && (
                <img src={GoldMedal} width={"40px"} alt="gold-medal" />
              )}
              {index === 1 && (
                <img src={SilverMedal} width={"40px"} alt="silver-medal" />
              )}
              {index === 2 && (
                <img src={BronzeMedal} width={"40px"} alt="bronze-medal" />
              )}
              {index >= 3 && (
                <img src={CommonMedal} width={"40px"} alt="common-medal" />
              )}
              <Text scale={300} fontWeight={500}>
                {user.name}
              </Text>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default MemberActivityContent;
